/**
 * ForgotPasswordPage - Password recovery flow
 * 
 * Architecture:
 * - Master key is random (not derived from password)
 * - Master key is wrapped by recovery key and stored on server
 * - Password reset: unwrap with recovery key → re-wrap with new password → update server
 * 
 * Two paths:
 * 1. User has recovery key → unwrap master key → set new password → re-wrap
 * 2. User without recovery key → OTP verification → data is lost, fresh start
 */
import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import Logo from '../components/logo/Logo';
import Spinner from '../components/Spinner';
import { supabase } from '../lib/supabase';
import { 
  isValidRecoveryKeyFormat,
  generateRecoveryKey,
  generateMasterKey,
  storeMasterKey,
  derivePasswordKey,
  wrapMasterKeyWithPassword,
  wrapMasterKeyWithRecovery,
  unwrapMasterKeyWithRecovery,
} from '../lib/crypto';

type Phase = 'email' | 'recovery-key' | 'otp' | 'new-password' | 'success';

type FlowType = 'recovery-key' | 'otp' | null;

interface Errors {
  form?: string | null;
  email?: string | null;
  recoveryKey?: string | null;
  otp?: string | null;
  password?: string | null;
  confirmPassword?: string | null;
}

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('email');
  const [flowType, setFlowType] = useState<FlowType>(null);
  const [email, setEmail] = useState('');
  const [recoveryKeyInput, setRecoveryKeyInput] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  
  // Data passed between phases
  const [wrappedByRecovery, setWrappedByRecovery] = useState<string | null>(null);
  const [recoveredMasterKey, setRecoveredMasterKey] = useState<Uint8Array | null>(null);
  const [newRecoveryKey, setNewRecoveryKey] = useState<string | null>(null);

  // Phase 1: Submit email to check for recovery key or send OTP
  const handleEmailSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Errors = {};
    if (!email.includes('@')) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    setErrors({});
    
    try {
      // Check if user has a recovery key stored
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/recovery-keys?email=${encodeURIComponent(email)}`
      );
      
      if (response.ok) {
        // User has recovery key - use that flow
        const data = await response.json();
        setWrappedByRecovery(data.wrapped_by_recovery);
        setFlowType('recovery-key');
        setPhase('recovery-key');
      } else if (response.status === 404) {
        // No recovery key - send OTP email
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email);
        if (resetError) {
          setErrors({ form: 'Failed to send reset code. Please try again.' });
          return;
        }
        setFlowType('otp');
        setPhase('otp');
        toast.success('Check your email for the 6-digit code');
      } else {
        setErrors({ form: 'Failed to fetch recovery information. Please try again.' });
      }
    } catch {
      setErrors({ form: 'Connection failed. Please check your internet connection.' });
    } finally {
      setLoading(false);
    }
  }, [email]);

  // Phase 2a: Verify recovery key and unwrap master key
  const handleRecoveryKeySubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Errors = {};
    if (!isValidRecoveryKeyFormat(recoveryKeyInput)) {
      newErrors.recoveryKey = 'Invalid recovery key format';
    }
    if (!wrappedByRecovery) {
      newErrors.form = 'Missing wrapped key. Please start over.';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    setErrors({});
    
    try {
      // Unwrap master key using recovery key
      const masterKey = await unwrapMasterKeyWithRecovery(
        wrappedByRecovery!,
        recoveryKeyInput
      );
      
      setRecoveredMasterKey(masterKey);
      setPhase('new-password');
    } catch {
      setErrors({ recoveryKey: 'Invalid recovery key. Please check and try again.' });
    } finally {
      setLoading(false);
    }
  }, [recoveryKeyInput, wrappedByRecovery]);

  // Phase 2b: Verify OTP code
  const handleOtpSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    const cleanOtp = otpInput.replace(/\s/g, '');
    if (cleanOtp.length !== 6) {
      setErrors({ otp: 'Please enter the 6-digit code' });
      return;
    }
    
    setLoading(true);
    setErrors({});
    
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: cleanOtp,
        type: 'recovery',
      });
      
      if (error) {
        setErrors({ otp: 'Invalid or expired code. Please try again.' });
        return;
      }
      
      // OTP verified - user now has a session
      setPhase('new-password');
    } catch {
      setErrors({ otp: 'Verification failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  }, [email, otpInput]);

  // Phase 3: Set new password
  const handleNewPasswordSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Errors = {};
    if (newPassword.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (flowType === 'recovery-key' && !recoveredMasterKey) {
      newErrors.form = 'Missing master key. Please start over.';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    setErrors({});
    
    try {
      if (flowType === 'otp') {
        // OTP flow: user already has session from verifyOtp
        // WARNING: Without recovery key, old encrypted data is lost!
        const { error: updateError } = await supabase.auth.updateUser({
          password: newPassword,
        });
        
        if (updateError) {
          setErrors({ form: updateError.message });
          return;
        }
        
        // Generate fresh master key (old data is lost without recovery key)
        const masterKey = generateMasterKey();
        await storeMasterKey(masterKey);
        
        // Generate new recovery key
        const { displayKey } = generateRecoveryKey();
        setNewRecoveryKey(displayKey);
        
        // Wrap master key with both password and recovery key
        const passwordKey = await derivePasswordKey(newPassword, email);
        const wrappedByPassword = await wrapMasterKeyWithPassword(masterKey, passwordKey);
        const newWrappedByRecovery = await wrapMasterKeyWithRecovery(masterKey, displayKey);
        
        // Save to server
        const session = await supabase.auth.getSession();
        if (session.data.session) {
          await fetch(`${import.meta.env.VITE_API_URL}/api/recovery-keys`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.data.session.access_token}`,
            },
            body: JSON.stringify({ 
              wrapped_by_password: wrappedByPassword,
              wrapped_by_recovery: newWrappedByRecovery,
            }),
          });
        }
        
        toast.success('Password updated successfully');
        setPhase('success');
      } else {
        // Recovery key flow: we have the original master key!
        // Just re-wrap it with the new password
        
        // First, trigger Supabase password reset email
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email);
        if (resetError) {
          console.warn('Supabase reset email failed:', resetError.message);
        }
        
        // Store master key locally
        await storeMasterKey(recoveredMasterKey!);
        
        // Wrap master key with new password
        const passwordKey = await derivePasswordKey(newPassword, email);
        const wrappedByPassword = await wrapMasterKeyWithPassword(recoveredMasterKey!, passwordKey);
        
        // Keep the same recovery key (user's existing one still works!)
        // We don't generate a new recovery key here because:
        // 1. The user just proved they have it
        // 2. The master key hasn't changed
        // 3. The existing wrapped_by_recovery is still valid
        setNewRecoveryKey(null); // Indicate no new recovery key
        
        // Note: We can't update wrapped_by_password on server without a session
        // The user will need to click the email link to complete the Supabase password reset
        // After that, they can log in with the new password
        // For now, we store the wrapped_by_password locally and update on next login
        
        // Store for next login (temporary - will be sent to server after email confirmation)
        localStorage.setItem('consl_pending_wrapped_by_password', wrappedByPassword);
        localStorage.setItem('consl_pending_email', email);
        
        setPhase('success');
        toast.success('Check your email to complete the password reset.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset password';
      setErrors({ form: message });
    } finally {
      setLoading(false);
    }
  }, [newPassword, confirmPassword, recoveredMasterKey, email, flowType]);

  // Resend OTP code
  const handleResendOtp = useCallback(async () => {
    setLoading(true);
    setErrors({});
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) {
        setErrors({ form: 'Failed to resend code. Please try again.' });
      } else {
        toast.success('New code sent to your email');
        setOtpInput('');
      }
    } catch {
      setErrors({ form: 'Failed to resend code.' });
    } finally {
      setLoading(false);
    }
  }, [email]);

  // Format recovery key input with dashes
  const handleRecoveryKeyChange = (value: string) => {
    const cleaned = value.replace(/-/g, '').toUpperCase();
    const parts = cleaned.match(/.{1,4}/g) || [];
    const formatted = parts.join('-').slice(0, 29);
    setRecoveryKeyInput(formatted);
    setErrors(prev => ({ ...prev, recoveryKey: null }));
  };

  // Format OTP input (just digits)
  const handleOtpChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    setOtpInput(cleaned);
    setErrors(prev => ({ ...prev, otp: null }));
  };

  // Handle success completion
  const handleSuccessComplete = () => {
    if (flowType === 'otp') {
      // User has session, go to app
      navigate('/');
    } else {
      // Recovery key flow, go to login
      navigate('/login');
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundImage: 'linear-gradient(in oklab 180deg, oklab(17.3% 0 0) 50%, oklab(14.5% 0 0) 100%)' }}
    >
      <div className="w-[300px]">
        {/* Header */}
        <div className="text-center">
          <div className="h-[64px] mb-2 flex items-center justify-center">
            <Logo variant="wordmark" text="Consl" size={32} ariaLabel="Consl" />
          </div>
          <h1 className="text-2xl font-normal text-primary mb-4">
            {phase === 'email' && 'Recover Your Account'}
            {phase === 'recovery-key' && 'Enter Recovery Key'}
            {phase === 'otp' && 'Enter Code'}
            {phase === 'new-password' && 'Set New Password'}
            {phase === 'success' && 'Password Updated'}
          </h1>
        </div>

        {/* Phase 1: Email */}
        {phase === 'email' && (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            {errors.form && (
              <p className="text-xs text-error">{errors.form}</p>
            )}
            
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: null })); }}
                placeholder="you@example.com"
                disabled={loading}
                className={`w-full h-[42px] px-4 border rounded-lg bg-bg-input text-primary text-base md:text-sm focus:outline-none focus:border-accent transition-[border-color] duration-150 ${errors.email ? 'border-error' : 'border-border-default'}`}
                autoFocus
              />
              {errors.email && <p className="text-xs text-error mt-1">{errors.email}</p>}
            </div>
            
            <button
              type="submit"
              disabled={loading || !email}
              className="w-full h-[42px] bg-accent text-tertiary rounded-lg hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 flex items-center justify-center active:scale-[0.97]"
            >
              {loading ? <Spinner size="sm" color="text-tertiary" /> : 'Continue'}
            </button>
            
            <div className="flex justify-start">
              <Link
                to="/login"
                className="text-sm text-secondary hover:text-primary transition-colors duration-150"
              >
                ← Back
              </Link>
            </div>
          </form>
        )}

        {/* Phase 2a: Recovery Key */}
        {phase === 'recovery-key' && (
          <form onSubmit={handleRecoveryKeySubmit} className="space-y-4">
            {errors.form && (
              <p className="text-xs text-error">{errors.form}</p>
            )}
            
            <div>
              <input
                type="text"
                value={recoveryKeyInput}
                onChange={(e) => handleRecoveryKeyChange(e.target.value)}
                placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
                disabled={loading}
                className={`w-full h-[42px] px-4 border rounded-lg bg-bg-input text-primary font-mono text-base md:text-sm text-center tracking-wider focus:outline-none focus:border-accent transition-[border-color] duration-150 ${errors.recoveryKey ? 'border-error' : 'border-border-default'}`}
                autoFocus
              />
              {errors.recoveryKey && <p className="text-xs text-error mt-1">{errors.recoveryKey}</p>}
            </div>
            
            <button
              type="submit"
              disabled={loading || recoveryKeyInput.replace(/-/g, '').length !== 24}
              className="w-full h-[42px] bg-accent text-tertiary rounded-lg hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 flex items-center justify-center active:scale-[0.97]"
            >
              {loading ? <Spinner size="sm" color="text-tertiary" /> : 'Verify Recovery Key'}
            </button>
            
            <div className="flex justify-start">
              <button
                type="button"
                onClick={() => { setPhase('email'); setErrors({}); }}
                className="text-sm text-secondary hover:text-primary transition-colors duration-150"
              >
                ← Back
              </button>
            </div>
          </form>
        )}

        {/* Phase 2b: OTP Code */}
        {phase === 'otp' && (
          <form onSubmit={handleOtpSubmit} className="space-y-4">
            {errors.form && (
              <p className="text-xs text-error">{errors.form}</p>
            )}
            
            <p className="text-sm text-secondary text-center">
              We sent a 6-digit code to <strong className="text-primary">{email}</strong>
            </p>
            
            <div className="p-3 rounded-lg bg-warning/10 border border-warning/20">
              <p className="text-xs text-warning">
                ⚠️ Without your recovery key, previously encrypted conversations cannot be recovered.
              </p>
            </div>
            
            <div>
              <input
                type="text"
                inputMode="numeric"
                value={otpInput}
                onChange={(e) => handleOtpChange(e.target.value)}
                placeholder="000000"
                disabled={loading}
                className={`w-full h-[42px] px-4 border rounded-lg bg-bg-input text-primary font-mono text-xl text-center tracking-[0.5em] focus:outline-none focus:border-accent transition-[border-color] duration-150 ${errors.otp ? 'border-error' : 'border-border-default'}`}
                autoFocus
                autoComplete="one-time-code"
              />
              {errors.otp && <p className="text-xs text-error mt-1">{errors.otp}</p>}
            </div>
            
            <button
              type="submit"
              disabled={loading || otpInput.length !== 6}
              className="w-full h-[42px] bg-accent text-tertiary rounded-lg hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 flex items-center justify-center active:scale-[0.97]"
            >
              {loading ? <Spinner size="sm" color="text-tertiary" /> : 'Verify Code'}
            </button>
            
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => { setPhase('email'); setErrors({}); setOtpInput(''); }}
                className="text-sm text-secondary hover:text-primary transition-colors duration-150"
              >
                ← Back
              </button>
              
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={loading}
                className="text-sm text-secondary hover:text-primary transition-colors duration-150 disabled:opacity-50"
              >
                Resend code
              </button>
            </div>
          </form>
        )}

        {/* Phase 3: New Password */}
        {phase === 'new-password' && (
          <form onSubmit={handleNewPasswordSubmit} className="space-y-4">
            {errors.form && (
              <p className="text-xs text-error">{errors.form}</p>
            )}
            
            <div>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setErrors(prev => ({ ...prev, password: null })); }}
                placeholder="New password (8+ characters)"
                disabled={loading}
                className={`w-full h-[42px] px-4 border rounded-lg bg-bg-input text-primary text-base md:text-sm focus:outline-none focus:border-accent transition-[border-color] duration-150 ${errors.password ? 'border-error' : 'border-border-default'}`}
                autoFocus
              />
              {errors.password && <p className="text-xs text-error mt-1">{errors.password}</p>}
            </div>
            
            <div>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setErrors(prev => ({ ...prev, confirmPassword: null })); }}
                placeholder="Confirm new password"
                disabled={loading}
                className={`w-full h-[42px] px-4 border rounded-lg bg-bg-input text-primary text-base md:text-sm focus:outline-none focus:border-accent transition-[border-color] duration-150 ${errors.confirmPassword ? 'border-error' : 'border-border-default'}`}
              />
              {errors.confirmPassword && <p className="text-xs text-error mt-1">{errors.confirmPassword}</p>}
            </div>
            
            <button
              type="submit"
              disabled={loading || newPassword.length < 8 || newPassword !== confirmPassword}
              className="w-full h-[42px] bg-accent text-tertiary rounded-lg hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 flex items-center justify-center active:scale-[0.97]"
            >
              {loading ? <Spinner size="sm" color="text-tertiary" /> : 'Update Password'}
            </button>
            
            <div className="flex justify-start">
              <button
                type="button"
                onClick={() => { 
                  setPhase(flowType === 'otp' ? 'otp' : 'recovery-key'); 
                  setErrors({}); 
                }}
                className="text-sm text-secondary hover:text-primary transition-colors duration-150"
              >
                ← Back
              </button>
            </div>
          </form>
        )}

        {/* Phase 4: Success */}
        {phase === 'success' && (
          <div className="space-y-4">
            {newRecoveryKey ? (
              <div className="p-4 rounded-lg bg-bg-card border border-border-default">
                <p className="text-sm text-secondary mb-2">
                  Your new recovery key (save this!):
                </p>
                <p className="font-mono text-primary text-center tracking-wider select-all">
                  {newRecoveryKey}
                </p>
              </div>
            ) : flowType === 'recovery-key' ? (
              <div className="p-4 rounded-lg bg-bg-card border border-border-default">
                <p className="text-sm text-secondary">
                  Your existing recovery key remains valid. Check your email to complete the password reset.
                </p>
              </div>
            ) : null}
            
            <button
              onClick={handleSuccessComplete}
              className="w-full h-[42px] bg-accent text-tertiary rounded-lg hover:bg-hover transition-colors duration-150 flex items-center justify-center active:scale-[0.97]"
            >
              {flowType === 'otp' ? 'Continue to App' : 'Return to Login'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
